import { useCallback, useEffect, useState } from 'react'
import { isStandalonePWA } from '@/utils/pwa'

/**
 * useInstallPrompt — captura o evento `beforeinstallprompt` do Chromium p/ oferecer
 * um botão de instalação DETERMINÍSTICO (não depende do banner automático do Chrome,
 * que é throttle-ado e suprimido após desinstalar).
 *
 * `beforeinstallprompt` só dispara em navegadores Chromium (Android/Chrome/Edge) quando
 * o PWA é instalável e ainda NÃO está instalado. iOS Safari não dispara → `canInstall`
 * fica false (instalação manual via Compartilhar → Adicionar à Tela de Início).
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function useInstallPrompt(): { canInstall: boolean; promptInstall: () => Promise<void> } {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(false)

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault() // impede o mini-infobar automático; usamos o botão próprio
      setDeferred(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setInstalled(true)
      setDeferred(null)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferred) return
    try {
      await deferred.prompt()
      await deferred.userChoice
    } catch {
      // best-effort — o evento é de uso único
    }
    setDeferred(null)
  }, [deferred])

  // Só oferece quando o navegador disse que é instalável, ainda não instalou,
  // e não estamos já rodando como PWA instalado.
  const canInstall = !!deferred && !installed && !isStandalonePWA()
  return { canInstall, promptInstall }
}
